import { useState, useEffect, useRef } from "react";
import { Send, MessageCircle, Trash2, Minus } from "lucide-react";
import "./ChatBot.css";
import axios from "axios";

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatMessages");
    return saved
      ? JSON.parse(saved)
      : [
          {
            from: "bot",
            text: "👋 Hi! How can I help you today?",
            time: new Date(),
          },
        ];
  });
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);

  const messagesEndRef = useRef(null);

  const toggleChat = () => setIsOpen((prev) => !prev);
  const handleSend = async () => {
    if (!input.trim()) return;

    const userMsg = { from: "user", text: input, time: new Date() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setTyping(true);

    try {
      // ✅ Axios automatically stringifies JSON if you pass an object
      const res = await axios.post(
        "http://187.127.131.218:8000/query",
        { project_id: "3", message: input },
        { headers: { "Content-Type": "application/json" } },
      );

      // ✅ With Axios, data is directly available
      const botReply = res.data.reply || "⚠️ No reply from server.";
      const botMsg = { from: "bot", text: botReply, time: new Date() };

      setMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      // ✅ Handle network/CORS errors
      console.error("API Error:", err.message);

      const botMsg = {
        from: "bot",
        text: "🤖 Sorry, I can't reach the server right now. Please try again later 🙂",
        time: new Date(),
      };
      setMessages((prev) => [...prev, botMsg]);
    } finally {
      setTyping(false);
    }
  };

  const clearChat = () => {
    setMessages([{ from: "bot", text: "🗑️ Chat cleared.", time: new Date() }]);
    localStorage.removeItem("chatMessages");
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    localStorage.setItem("chatMessages", JSON.stringify(messages));
  }, [messages]);

  const formatDate = (date) => {
    const today = new Date();
    const msgDate = new Date(date);
    if (msgDate.toDateString() === today.toDateString()) return "Today";
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (msgDate.toDateString() === yesterday.toDateString()) return "Yesterday";
    return msgDate.toLocaleDateString();
  };

  let lastDate = "";

  return (
    <div className="chatbot-container">
      {!isOpen && (
        <button className="chatbot-btn" onClick={toggleChat}>
          <MessageCircle size={28} />
        </button>
      )}

      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <span>AI Assistant</span>
            <div className="chatbot-actions">
              <button onClick={clearChat} title="Clear Chat">
                <Trash2 size={16} />
              </button>
              <button onClick={toggleChat} title="Minimize">
                <Minus size={16} />
              </button>
            </div>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, i) => {
              const showDate = formatDate(msg.time) !== lastDate;
              lastDate = formatDate(msg.time);
              return (
                <div key={i}>
                  {showDate && <div className="chat-date">{lastDate}</div>}
                  <div className={`chat-msg ${msg.from}`}>
                    <div className="avatar">
                      {msg.from === "user" ? "U" : "AI"}
                    </div>
                    <div className="msg-content">
                      <p>{msg.text}</p>
                      <small>
                        {new Date(msg.time).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </div>
                  </div>
                </div>
              );
            })}
            {typing && (
              <div className="typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button onClick={handleSend}>
              <Send size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
